import React from "react";
import { withStyles } from "@material-ui/core/styles";
import { Typography, Button, Grid } from "@material-ui/core";
import { observer, inject } from "mobx-react";
import Select from "../../../../../../common/StyledSelect";
import styles from "./Styles";
import CustomTextField from "../../common/CustomTextField";
import {
  MIN_VALUE,
  HAZMAT_FORM_CONSTANTS,
  MAX_VALUE_CONSTRAINTS,
  HAZMAT_EMERGENCY_NUMBER,
} from "../../common/constants";
import constants from "../../EditStopDialog/constants";
import { acceptOnlyNumber } from "../../common/utils";

@inject("DispatchStore")
@observer
class HazmatForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hazmatErrorInfo: {},
      hazmatInfo: this.getDefaultHazmatInfo(),
      isUNPGInputWarning: false,
    };
    this.allHazmatInfo = this.props.DispatchStore.hazmatInfoList;
    this.packageTypeList = this.props.DispatchStore.packageCodes;
    this.formattedHazmatInfo = this.populateFormattedHazmatInfo(
      this.allHazmatInfo
    );
  }

  getDefaultHazmatInfo = () => {
    if (Object.keys(this.props.hazmatInfo).length === 0) {
      return {
        unNumber: null,
        pgNumber: null,
        pieces: null,
        weight: null,
        packageCode: null,
        bulk: "No",
        emergencyPhone: HAZMAT_EMERGENCY_NUMBER,
      };
    } else {
      const unNumber = this.props.hazmatInfo.unNumber.slice(2, 6);
      return {
        ...this.props.hazmatInfo,
        pgNumber:
          this.props.hazmatInfo.pgNumber === ""
            ? "None"
            : this.props.hazmatInfo.pgNumber,
        unNumber: unNumber,
      };
    }
  };

  handleChange = (name) => (value) => {
    if (["packageCode", "bulk", "pgNumber"].includes(name)) {
      value = value && value.label;
    }
    if (name === "emergencyPhone") {
      value = value.replace(/\D/g, "");
    }
    this.setState((state) => {
      return {
        hazmatInfo: {
          ...state.hazmatInfo,
          [name]: value,
        },
        hazmatErrorInfo: {
          ...state.hazmatErrorInfo,
          [name]: this.getFieldError(name, value),
        },
      };
    });
  };

  getHazmatCombination = (unNumber, pgNumber, hazmatInfo) => {
    return hazmatInfo[unNumber].find((data) => data.packageGroup === pgNumber);
  };

  validatePGUGCombination = (unNumber, pgNumber) => {
    if (!this.isValidUNNumber(unNumber)) {
      return null;
    }
    const pgNumberValue = this.getPGNumberValue(pgNumber);
    const combination = this.getHazmatCombination(
      HAZMAT_FORM_CONSTANTS.UN_PREFIX + unNumber,
      pgNumberValue,
      this.formattedHazmatInfo
    );
    if (!combination) {
      this.setState((state) => ({
        hazmatErrorInfo: {
          ...state.hazmatErrorInfo,
          unNumber: null,
        },
        isUNPGInputWarning: true,
      }));
    } else {
      const isShippable = combination.noShipFlag !== "N";
      this.setState((state) => ({
        hazmatErrorInfo: {
          ...state.hazmatErrorInfo,
          unNumber: isShippable
            ? null
            : HAZMAT_FORM_CONSTANTS.UN_NUMBER.ERROR_TEXTS
                .UN_NUMBER_NOT_SHIPPABLE_TEXT,
        },
        isUNPGInputWarning: false,
      }));
    }
  };

  getPGNumberValue = (label) => {
    if (label)
      return HAZMAT_FORM_CONSTANTS.PG_NUMBER_LIST.find(
        (data) => data.label === label || data.value === label
      )?.value;
  };

  getBulkDetailsFromLabel = (label) => {
    if (label)
      return HAZMAT_FORM_CONSTANTS.BULK_LIST.find(
        (data) => data.label === label || data.value === label
      );
    return { label: "No", value: false };
  };

  validateUNNumber = (value) => {
    const error = this.isValidUNNumberErrorText(value);
    if (!error) {
      const pgNumber = this.getPGNumberValue(this.state.hazmatInfo.pgNumber);
      const combination = this.getHazmatCombination(
        HAZMAT_FORM_CONSTANTS.UN_PREFIX + value,
        pgNumber,
        this.formattedHazmatInfo
      );
      if (!combination) {
        this.setState({
          isUNPGInputWarning: true,
        });
        return null;
      }
      const isShippable = combination.noShipFlag !== "N";
      return isShippable
        ? null
        : HAZMAT_FORM_CONSTANTS.UN_NUMBER.ERROR_TEXTS
            .UN_NUMBER_NOT_SHIPPABLE_TEXT;
    }
    return error;
  };

  isValidUNNumber = (value) => {
    return this.isValidUNNumberErrorText(value) ? false : true;
  };

  disableWarning = () => {
    if (this.state.isUNPGInputWarning) {
      this.setState({
        isUNPGInputWarning: false,
      });
    }
  };

  isValidUNNumberErrorText = (value) => {
    if (value) {
      value = value.trim();
      if (value.length !== 4) {
        this.disableWarning();
        return HAZMAT_FORM_CONSTANTS.UN_NUMBER.ERROR_TEXTS
          .UN_NUMBER_LENGTH_TEXT;
      }
      if (
        Object.keys(this.formattedHazmatInfo || {}).length > 0 &&
        !this.formattedHazmatInfo?.hasOwnProperty(
          HAZMAT_FORM_CONSTANTS.UN_PREFIX + value
        )
      ) {
        this.disableWarning();
        return HAZMAT_FORM_CONSTANTS.UN_NUMBER.ERROR_TEXTS
          .UN_NUMBER_INVALID_TEXT;
      }
      return null;
    }
    this.disableWarning();
    return HAZMAT_FORM_CONSTANTS.UN_NUMBER.ERROR_TEXTS.UN_NUMBER_REQUIRED_TEXT;
  };

  populateFormattedHazmatInfo = (allHazmatInfo) => {
    const hazmatInfo = {};
    allHazmatInfo.map((value) => {
      if (hazmatInfo.hasOwnProperty(value.unid)) {
        hazmatInfo[value.unid].push({
          packageGroup: value.packageGroup,
          noShipFlag: value.noShipFlag,
        });
      } else {
        hazmatInfo[value.unid] = [
          {
            packageGroup: value.packageGroup,
            noShipFlag: value.noShipFlag,
          },
        ];
      }
    });
    return hazmatInfo;
  };

  validateHazmatWeight = (value) => {
    const { packageWeight, totalHazmatWeight, hazmatInfo } = this.props;
    const computedTotalHazmatWeight =
      parseInt(hazmatInfo.weight) > 0
        ? totalHazmatWeight - hazmatInfo.weight
        : totalHazmatWeight;
    if (parseInt(value) < parseInt(hazmatInfo.weight)) {
      return null;
    }
    if (computedTotalHazmatWeight + parseInt(value) > packageWeight) {
      return HAZMAT_FORM_CONSTANTS.WEIGHT_ERROR_TEXT;
    }
    return null;
  };

  getFieldError = (field, value) => {
    const trimValue = value && typeof value === "string" ? value.trim() : value;
    switch (field) {
      case "pieces":
        return value ? null : "* Pieces is required";
      case "weight":
        return value
          ? this.validateHazmatWeight(value)
          : "* Weight is required";
      case "unNumber":
        const error = this.validateUNNumber(value);
        return !error ? null : error;
      case "pgNumber":
        this.validatePGUGCombination(this.state.hazmatInfo.unNumber, value);
        return trimValue ? null : "* PG Number is required";
      case "packageCode":
        return trimValue ? null : "* Package Type is required";
      case "emergencyPhone":
        return trimValue ? null : "* Emergency Number is required";
      case "bulk":
        return trimValue ? null : "* Bulk is required";
      default:
        return null;
    }
  };

  getErrorInfo = () => {
    let errorInfo = {};
    Object.keys(this.state.hazmatInfo).forEach((fieldName) => {
      errorInfo[fieldName] = this.getFieldError(
        fieldName,
        this.state.hazmatInfo[fieldName]
      );
    });
    this.setState({
      hazmatErrorInfo: errorInfo,
    });
    return errorInfo;
  };

  getPackageDetailFromLabel = (packageType) => {
    const packageCodesStaticList =
      this.props.DispatchStore.getStaticClientData().packageCodes;
    return packageCodesStaticList.find(
      (packageCode) =>
        packageCode.description === packageType ||
        packageCode.code === packageType
    );
  };

  populateFormattedPackageDetails = (hazmatInfo) => {
    return {
      ...hazmatInfo,
      unNumber: HAZMAT_FORM_CONSTANTS.UN_PREFIX + hazmatInfo.unNumber,
      packageCode: this.getPackageDetailFromLabel(hazmatInfo.packageCode)?.code,
      pgNumber: this.getPGNumberValue(hazmatInfo.pgNumber),
      pieces: parseInt(hazmatInfo.pieces),
      weight: parseInt(hazmatInfo.weight),
      bulk: this.getBulkDetailsFromLabel(hazmatInfo.bulk)?.value,
      emergencyPhone: acceptOnlyNumber(hazmatInfo.emergencyPhone),
    };
  };

  saveHazmatDetails = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    if (!error) {
      this.props.saveHazmatEntry(
        this.populateFormattedPackageDetails(this.state.hazmatInfo)
      );
    }
  };

  isUGPGCombinationWarning = () => {
    const {
      isUNPGInputWarning,
      hazmatInfo: { pgNumber },
    } = this.state;
    return pgNumber && isUNPGInputWarning;
  };

  mask = (rawValue) => {
    return rawValue.length <= 10
      ? constants.PHONE_NUMBER_MASK
      : constants.ELEVEN_DIGITS_PHONE_NUMBER_MASK;
  };

  render() {
    const { classes } = this.props;
    return (
      <div>
        <div id="hazmatDetails">
          <Typography variant="subtitle1" className={classes.headerLabel}>
            HAZMAT DETAILS
          </Typography>
          <div>
            <Grid container>
              <Grid item xs={6}>
                <CustomTextField
                  id="unNumber"
                  handleChange={this.handleChange("unNumber")}
                  value={this.state.hazmatInfo.unNumber}
                  error={this.state.hazmatErrorInfo.unNumber}
                  label="4 - digit UN Number"
                  inputProps={{ maxLength: 4, focused: true }}
                  showWarning={this.isUGPGCombinationWarning()}
                />
              </Grid>
              <Grid item xs={6}>
                <div className={classes.inputClass}>
                  <Select
                    textFieldProps={{
                      id: "pgNumber",
                      label: "PG Number",
                      variant: "outlined",
                      helperText: this.state.hazmatErrorInfo.pgNumber,
                    }}
                    showWarning={this.isUGPGCombinationWarning()}
                    value={this.state.hazmatInfo.pgNumber}
                    error={Boolean(this.state.hazmatErrorInfo.pgNumber)}
                    onChange={this.handleChange("pgNumber")}
                    data={HAZMAT_FORM_CONSTANTS.PG_NUMBER_LIST}
                  />
                </div>
              </Grid>
            </Grid>
            {this.isUGPGCombinationWarning() && (
              <div>
                <span className={classes.inputRowWarningTextColor}>
                  {
                    HAZMAT_FORM_CONSTANTS.UN_NUMBER.WARNING_TEXTS
                      .UN_NUMBER_WARNING_TEXT
                  }
                </span>
              </div>
            )}
          </div>
        </div>
        <div id="packagingDetails">
          <Typography variant="subtitle1" className={classes.headerLabel}>
            PACKAGING DETAILS
          </Typography>
          <Grid container>
            <Grid item xs={6}>
              <CustomTextField
                id="pieces"
                handleChange={this.handleChange("pieces")}
                value={this.state.hazmatInfo.pieces}
                error={this.state.hazmatErrorInfo.pieces}
                type="number"
                label="Pieces"
                inputProps={{
                  min: MIN_VALUE.PICKUP_PRO_FORM.PIECES,
                  max: MAX_VALUE_CONSTRAINTS.PICKUP_PRO_FORM.PIECES,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.inputClass}>
                <CustomTextField
                  id="weight"
                  handleChange={this.handleChange("weight")}
                  value={this.state.hazmatInfo.weight}
                  error={this.state.hazmatErrorInfo.weight}
                  type="number"
                  label="Weight (lbs)"
                  inputProps={{
                    min: MIN_VALUE.PICKUP_PRO_FORM.WEIGHT,
                    max: MAX_VALUE_CONSTRAINTS.PICKUP_PRO_FORM.HAZMAT_WEIGHT,
                  }}
                />
              </div>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={6}>
              <Select
                textFieldProps={{
                  id: "packageType",
                  label: "Package Type",
                  variant: "outlined",
                  helperText: this.state.hazmatErrorInfo.packageCode,
                }}
                value={
                  this.getPackageDetailFromLabel(
                    this.state.hazmatInfo.packageCode
                  )?.description
                }
                error={Boolean(this.state.hazmatErrorInfo.packageCode)}
                onChange={this.handleChange("packageCode")}
                data={this.packageTypeList}
              />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.inputClass}>
                <Select
                  textFieldProps={{
                    id: "bulk",
                    label: "Bulk",
                    variant: "outlined",
                    helperText: this.state.hazmatErrorInfo.bulk,
                  }}
                  value={
                    this.getBulkDetailsFromLabel(this.state.hazmatInfo.bulk)
                      ?.label
                  }
                  error={Boolean(this.state.hazmatErrorInfo.bulk)}
                  onChange={this.handleChange("bulk")}
                  data={HAZMAT_FORM_CONSTANTS.BULK_LIST}
                />
              </div>
            </Grid>
          </Grid>
        </div>
        <div>
          <CustomTextField
            className={classes.fullWidth}
            id="emergencyPhone"
            handleChange={this.handleChange("emergencyPhone")}
            value={this.state.hazmatInfo.emergencyPhone}
            error={this.state.hazmatErrorInfo.emergencyPhone}
            label="Emergency Number"
            mask={this.mask}
          />
        </div>
        <div className={classes.hazmatContainer}>
          <Button
            autoFocus
            color="primary"
            variant="outlined"
            className={classes.actionButton}
            onClick={this.props.closeHazmatForm}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            className={classes.actionButton}
            onClick={this.saveHazmatDetails}
          >
            Save Hazmat Details
          </Button>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(HazmatForm);
